import { makeAutoObservable, runInAction, toJS } from "mobx";
import {
  setInitialValueAndRequired,
  isBlank,
  focusAtElement,
} from "../utils/commonUtils";
import {
  BANKING,
  EMAIL,
  EMPTY_ERROR_CODE,
  INSURANCE,
  PHONE,
  WEB_MESSAGE,
} from "../utils/enums";

class FeedbackStore {
  feedback = null;
  showErrorSummary = false;

  constructor() {
    makeAutoObservable(this);
    this.initializeFeedback();
  }

  initializeFeedback = () => {
    this.feedback = {
      type: setInitialValueAndRequired("", true),
      target: setInitialValueAndRequired("", true),
      subject: setInitialValueAndRequired("", true),
      topic: setInitialValueAndRequired("", true),
      text: setInitialValueAndRequired("", true),
      contactCustomer: setInitialValueAndRequired(false, false),
      contactMethod: setInitialValueAndRequired("", true),
      phone: setInitialValueAndRequired("", true),
      email: setInitialValueAndRequired("", true),
      reachability: setInitialValueAndRequired("", false),
      customerInfo: setInitialValueAndRequired(false, false),
      name: setInitialValueAndRequired("", true),
      address: setInitialValueAndRequired("", true),
      postalCode: setInitialValueAndRequired("", true),
    };
  };

  reset = () => {
    this.initializeFeedback();
  };

  updateValue = (key, value) => {
    this.feedback[key].value = value;
    this.feedback[key].dirty = true;
  };

  updateIncluded = (key, included) => {
    this.feedback[key].included = included;
  };

  updateError = (key, error) => {
    this.feedback[key].error = error;
  };

  updateTopic = (value, msgs) => {
    let topic;
    if (isBlank(value)) {
      topic = "";
    } else {
      topic = `${msgs(`option.${value}`)}`;
    }
    this.feedback.topic.value = topic;
  };

  updateLoggedInUserInfo = (phone, email, address, postalCode) => {
    if (this.isDirty) return;

    const info = [
      { key: "phone", value: phone },
      { key: "email", value: email },
      { key: "address", value: address },
      { key: "postalCode", value: postalCode },
    ];

    Object.values(info).forEach((item) => {
      if (!isBlank(item.value)) {
        runInAction(() => {
          this.feedback[item.key].value = item.value;
          this.feedback[item.key].included = true;
        });
      }
    });
  };

  displayErrorSummary = (msgs) => {
    // Add errors to empty required fields
    Object.entries(toJS(this.feedback)).map((item) => {
      const key = item[0];
      const v = item[1];
      if (v.included && v.required && v.value !== false && isBlank(v.value)) {
        const error = new Error(msgs(`${key}.error.empty`));
        error.code = EMPTY_ERROR_CODE;
        this.feedback[key].error = error;
      }
      return true;
    });

    this.showErrorSummary = true;
    focusAtElement("#CFF-errors");
  };

  get isInvalid() {
    return Object.values(toJS(this.feedback)).some(
      (v) =>
        (v.error && v.included) ||
        (v.included && v.required && v.value !== false && isBlank(v.value))
    );
  }

  get invalidFields() {
    const invalidFields = Object.entries(toJS(this.feedback)).filter(
      (item) => item[1].error && item[1].error.code && item[1].included
    );

    return invalidFields.map((field) => ({ key: field[0], value: field[1] }));
  }

  get isDirty() {
    return Object.values(this.feedback).some((v) => v.dirty);
  }

  get showContactDetails() {
    return (
      this.feedback.contactCustomer.value &&
      this.feedback.contactMethod.value !== WEB_MESSAGE
    );
  }

  get contactByPhone() {
    return (
      this.feedback.contactCustomer.value &&
      this.feedback.contactMethod.value === PHONE
    );
  }

  get contactByEmail() {
    return (
      this.feedback.contactCustomer.value &&
      this.feedback.contactMethod.value === EMAIL
    );
  }

  get displayFeedbackSubject() {
    return this.isBankFeedback || this.isInsuranceFeedback;
  }

  get isBankFeedback() {
    return this.feedback.target.value === BANKING;
  }

  get isInsuranceFeedback() {
    return this.feedback.target.value === INSURANCE;
  }
}

const feedbackStore = new FeedbackStore();

export default feedbackStore;
